import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, Plus, Save, Trash2, Edit3, Eye, ChevronUp, ChevronDown, EyeOff, X,
} from "lucide-react";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { renderMarkdownSafe } from "../../lib/safeMarkdown";
import { extractApiError } from "../../lib/apiError";

export default function WhyChooseUsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [reordering, setReordering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/why-choose-us`);
      setItems(res.data || []);
    } catch (e) {
      toast.error(extractApiError(e, "Failed to load"));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (payload, id) => {
    try {
      if (id) await axios.put(`${API}/admin/why-choose-us/${id}`, payload);
      else await axios.post(`${API}/admin/why-choose-us`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(extractApiError(e, "Failed to save")); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.question}"?`)) return;
    try {
      await axios.delete(`${API}/admin/why-choose-us/${item.item_id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(extractApiError(e, "Failed to delete")); }
  };

  const togglePublished = async (item) => {
    try {
      await axios.put(`${API}/admin/why-choose-us/${item.item_id}`, { published: !item.published });
      load();
    } catch (e) { toast.error(extractApiError(e, "Failed")); }
  };

  const move = async (index, dir) => {
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    const list = [...items];
    const [m] = list.splice(index, 1);
    list.splice(newIdx, 0, m);
    setItems(list);
    setReordering(true);
    try {
      await axios.post(`${API}/admin/why-choose-us/reorder`, { ordered_ids: list.map(i => i.item_id) });
    } catch (e) { toast.error(extractApiError(e, "Reorder failed")); load(); }
    finally { setReordering(false); }
  };

  return (
    <div data-testid="wcu-tab" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#C5A059]" /> Why Choose Us
          </h2>
          <p className="text-white/40 text-xs mt-1">
            Public page at <a href="/why-choose-us" target="_blank" rel="noreferrer" className="text-[#C5A059] hover:underline">/why-choose-us</a>. Markdown supported in content.
          </p>
        </div>
        <Button onClick={() => setEditing("new")} className="btn-gold" data-testid="wcu-add-btn">
          <Plus className="w-4 h-4 mr-2" /> Add reason
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="spinner" /></div>
      ) : items.length === 0 ? (
        <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-8 text-center text-white/50">No items yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((it, idx) => (
            <Row key={it.item_id} item={it} index={idx} total={items.length} disabled={reordering}
              onEdit={() => setEditing(it)}
              onDelete={() => handleDelete(it)}
              onToggle={() => togglePublished(it)}
              onUp={() => move(idx, -1)} onDown={() => move(idx, 1)} />
          ))}
        </div>
      )}

      {editing && (
        <Editor initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={(p) => save(p, editing === "new" ? null : editing.item_id)} />
      )}
    </div>
  );
}

function Row({ item, index, total, disabled, onEdit, onDelete, onToggle, onUp, onDown }) {
  return (
    <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4 flex items-start gap-3" data-testid={`wcu-row-${item.item_id}`}>
      <div className="flex flex-col gap-1">
        <button onClick={onUp} disabled={disabled || index === 0} className="text-white/30 hover:text-[#C5A059] disabled:opacity-20" aria-label="Move up"><ChevronUp className="w-4 h-4" /></button>
        <button onClick={onDown} disabled={disabled || index === total - 1} className="text-white/30 hover:text-[#C5A059] disabled:opacity-20" aria-label="Move down"><ChevronDown className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] text-white/30 font-mono">#{index + 1}</span>
          {!item.published && <span className="text-[10px] uppercase text-orange-400 bg-orange-400/10 px-2 py-0.5 rounded-sm">Hidden</span>}
        </div>
        <p className="text-white font-medium truncate">{item.question}</p>
        <p className="text-white/40 text-xs mt-1 line-clamp-2">{item.content.replace(/[#*_`>-]/g, "").slice(0, 160)}</p>
      </div>
      <div className="flex gap-1 shrink-0">
        <Button variant="ghost" size="sm" onClick={onToggle} className="text-white/60 hover:text-[#C5A059]" data-testid={`wcu-toggle-${item.item_id}`}>{item.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</Button>
        <Button variant="ghost" size="sm" onClick={onEdit} className="text-white/60 hover:text-[#C5A059]" data-testid={`wcu-edit-${item.item_id}`}><Edit3 className="w-4 h-4" /></Button>
        <Button variant="ghost" size="sm" onClick={onDelete} className="text-white/60 hover:text-red-400" data-testid={`wcu-delete-${item.item_id}`}><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function Editor({ initial, onClose, onSave }) {
  const [question, setQuestion] = useState(initial?.question || "");
  const [content, setContent] = useState(initial?.content || "");
  const [published, setPublished] = useState(initial ? !!initial.published : true);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const previewHtml = useMemo(() => ({ __html: renderMarkdownSafe(content, "wcu-admin-preview") }), [content]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!question.trim()) { toast.error("Title is required"); return; }
    if (!content.trim()) { toast.error("Content is required"); return; }
    setSaving(true);
    try { await onSave({ question: question.trim(), content: content.trim(), published }); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="wcu-editor-modal">
      <form onSubmit={submit} className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h3 className="text-lg font-semibold text-white">{initial ? "Edit reason" : "New reason"}</h3>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white" data-testid="wcu-editor-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-white/50 text-[10px] uppercase mb-1 block">Title</label>
            <Input value={question} onChange={(e) => setQuestion(e.target.value)} className="input-dark" maxLength={500} required data-testid="wcu-editor-title" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-white/50 text-[10px] uppercase">Content (Markdown)</label>
              <button type="button" onClick={() => setPreview(p => !p)} className="text-[#C5A059] text-xs hover:underline" data-testid="wcu-editor-toggle-preview">{preview ? "Edit" : "Preview"}</button>
            </div>
            {preview ? (
              <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-4 min-h-[260px] text-white/80 prose-custom prose-sm" dangerouslySetInnerHTML={previewHtml} />
            ) : (
              <textarea value={content} onChange={(e) => setContent(e.target.value)} className="w-full h-64 bg-[#0A0A0A] border border-[#333] rounded-sm p-4 text-white/80 text-sm font-mono resize-y focus:border-[#C5A059]/50 focus:outline-none" maxLength={20000} spellCheck="false" required data-testid="wcu-editor-content" />
            )}
          </div>
          <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
            <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} className="accent-[#C5A059]" data-testid="wcu-editor-published" /> Published
          </label>
        </div>
        <div className="p-5 border-t border-white/5 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} className="text-white/60">Cancel</Button>
          <Button type="submit" disabled={saving} className="btn-gold" data-testid="wcu-editor-save"><Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save"}</Button>
        </div>
      </form>
    </div>
  );
}
